import { AddressType } from '@synergy/models';
import { CommentNoteIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { PoolButton, PoolFormattedMessage, PoolModal, PoolTypography, ValueOrNDash } from '@synergy/components';
import { Theme, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useComponentSize } from '@synergy/components-next';
import { useState } from 'react';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { AddressFormValues } from '../../models/address-form-values';
import { GetUsageTypeIcon } from '../get-usage-type-icon';

const useStyles = makeStyles((theme: Theme) => ({
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    titleDetails: {
        display: 'flex',
        gap: theme.spacing(),
    },
    titleType: {
        color: theme.palette.grey[700],
    },
    titleMainAddress: {
        color: theme.palette.primary.main,
    },
}));

/** Usage, note and the row menu. */
const FIXED_COLUMNS_WIDTH = 190;
const TITLE_MIN_WIDTH = 240;
const TITLE_MAX_WIDTH = 420;
const STREET_MIN_WIDTH = 190;
const ZIP_MIN_WIDTH = 150;
const COUNTRY_MIN_WIDTH = 140;
/** "Österreich, Salzburg" in full. */
const COUNTRY_WIDTH = 190;

/*
 * The title lived off what the other columns left: a thousand pixels of
 * mostly empty title next to a clipped street on a wide screen (R-206), and
 * a clipped "Österreich, Salzbu…" beside it on a narrow one (R-210). The
 * room above the floors goes first to the country, then to title, street
 * and place alike until the title has its 420 pixels, and the rest to
 * street and place. A table cell only clips its text at a measure in
 * pixels, so the split is worked out from the table's own width; the title
 * takes whatever is left of it.
 */
const addressColumnWidths = (tableWidth: number): { street: number; zip: number; country: number } => {
    let room = Math.max(
        0,
        tableWidth - FIXED_COLUMNS_WIDTH - TITLE_MIN_WIDTH - STREET_MIN_WIDTH - ZIP_MIN_WIDTH - COUNTRY_MIN_WIDTH
    );

    const country = COUNTRY_MIN_WIDTH + Math.min(room, COUNTRY_WIDTH - COUNTRY_MIN_WIDTH);
    room -= country - COUNTRY_MIN_WIDTH;

    const alike = Math.min(Math.floor(room / 3), TITLE_MAX_WIDTH - TITLE_MIN_WIDTH);
    room -= 3 * alike;

    const street = STREET_MIN_WIDTH + alike + Math.floor(room * 0.55);

    return { street, zip: ZIP_MIN_WIDTH + alike + (room - Math.floor(room * 0.55)), country };
};

interface AddressTableProps {
    addresses: AddressFormValues[];
    onNewAddressClick: () => void;
    onAddressEdit: (values: AddressFormValues) => void;
    onStandardAddressClick: (values: AddressFormValues) => void;
    onAddressDeleteClick: (values: AddressFormValues) => void;
}

export const AddressTable = (props: AddressTableProps) => {
    const { addresses, onNewAddressClick, onAddressEdit, onStandardAddressClick, onAddressDeleteClick } = props;
    const [modalAddress, setModalAddress] = useState<AddressFormValues | null>(null);
    const classes = useStyles();
    const { measuredRef, width: tableWidth } = useComponentSize();
    const widths = addressColumnWidths(tableWidth);

    const addressColumns: PaloColumn<AddressFormValues>[] = [
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.label.title" />,
            cell: address => (
                <div className={classes.titleContainer}>
                    <ValueOrNDash value={address.title} />
                    <div className={classes.titleDetails}>
                        <PoolTypography
                            typography="caption"
                            component="span"
                            className={classes.titleType}
                            id={AddressType.Work === address.type ? 'app.common.work' : 'app.common.private'}
                        />

                        {address.isDefaultAddress && (
                            <PoolTypography
                                typography="caption"
                                component="span"
                                className={classes.titleMainAddress}
                                id="app.contactsAndOrganizations.detail.standardAddress"
                            />
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'street',
            type: 'name',
            width: `${widths.street}px`,
            head: <FormattedMessage id="app.table.column.streetAndNumber" />,
            cell: address => [address.street, address.number, address.addition].filter(Boolean).join(' '),
        },
        {
            key: 'zip',
            type: 'name',
            width: `${widths.zip}px`,
            head: <FormattedMessage id="app.table.column.zipAndLocation" />,
            cell: address => `${address.zip ?? ''} ${address.city ?? ''}`.trim(),
        },
        {
            key: 'country',
            type: 'classification',
            width: `${widths.country}px`,
            head: <FormattedMessage id="app.common.country" />,
            // The region shares the country's cell: it only makes sense next to it,
            // and a mismatch between the two blocks saving the whole account.
            cell: address => [address.country?.name, address.region?.name].filter(Boolean).join(', '),
        },
        {
            key: 'usage',
            type: 'classification',
            width: '80px',
            head: <FormattedMessage id="app.table.column.usage" />,
            cell: address => <GetUsageTypeIcon usageType={address.usageType} />,
        },
        {
            key: 'note',
            type: 'icon',
            wrap: true,
            head: <FormattedMessage id="app.table.column.notes" />,
            cell: address =>
                address.note ? (
                    <Tooltip title={address.note} placement="bottom">
                        <div>
                            <CommentNoteIcon />
                        </div>
                    </Tooltip>
                ) : null,
        },
    ];

    const rowActions = (address: AddressFormValues): PaloRowAction[] => [
        { label: <FormattedMessage id="app.common.edit" />, onClick: () => onAddressEdit(address) },
        ...(address.isDefaultAddress
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.contactsAndOrganizations.detail.standardAddress" />,
                      onClick: () => onStandardAddressClick(address),
                  },
              ]),
        { label: <FormattedMessage id="app.common.delete" />, onClick: () => setModalAddress(address) },
    ];

    const handleAddressDeleteClick = () => {
        if (modalAddress) {
            onAddressDeleteClick(modalAddress);
            setModalAddress(null);
        }
    };

    const actionChildren = [
        <PoolButton key="1" onClick={() => setModalAddress(null)} variant="text" styleType="firstLevel">
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <PoolButton key="2" onClick={handleAddressDeleteClick} color="secondary" styleType="firstLevel">
            <FormattedMessage id="app.common.delete" />
        </PoolButton>,
    ];

    return (
        <>
            <div ref={measuredRef}>
                {addresses.length > 0 && (
                    <PaloListView<AddressFormValues, number>
                        embedded={true}
                        columns={addressColumns}
                        items={addresses}
                        // An address the user has just added has no id yet, so it
                        // borrows a negative one from its place in the list.
                        getRowId={address => address.id ?? -1 - addresses.indexOf(address)}
                        emptyLabel={<FormattedMessage id="app.contactsAndOrganizations.detail.addAddress" />}
                        rowActions={rowActions}
                    />
                )}
            </div>
            <PoolButton styleType="thirdLevel" startIcon={<AddIcon color="primary" />} onClick={onNewAddressClick}>
                <FormattedMessage id="app.contactsAndOrganizations.detail.addAddress" />
            </PoolButton>

            <PoolModal
                title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteAddress" />}
                isVisible={!!modalAddress}
                handleClose={() => setModalAddress(null)}
                actionChildren={actionChildren}
                hasModalTypography={true}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.detail.deleteAddressText"
                    values={{
                        value: `${modalAddress?.street} ${modalAddress?.number ?? ''} ${
                            modalAddress?.addition ?? ''
                        }, ${modalAddress?.zip} ${modalAddress?.city}`,
                    }}
                />
            </PoolModal>
        </>
    );
};
